import { useEffect, useRef, useState } from 'react'
import { useDismiss } from '../lib/useDismiss'
import type { Me } from '../lib/me'
import { can } from '../lib/me'
import {
  loadComments,
  addComment,
  retractComment,
  moderateComment,
  markCommentOfficial,
  loadRetractionReasons,
  loadCommentOriginal,
  requestCommentTranslation,
  type WikiComment,
} from '../lib/wiki'
import { TranslatedLine, useOriginal } from './TranslatedLine'
import { loadCheckNotes, replyNotes, type CheckNote as Note } from '../lib/moderation'
import { CheckNote } from './moderation/CheckNote'
import { VoteButton } from './wiki/VoteButton'
import { ReactionBar } from './ReactionBar'
import { MentionBox } from './MentionBox'
import {
  EMPTY_DRAFT,
  splitMentions,
  toStorage,
  type MentionDraft,
  type MentionMap,
} from '../lib/mentions'
import { messageOf } from '../lib/errors'
import { useHubLang } from '../lib/i18n'
import { THREAD, CHECK_NOTE, type ThreadSurfaceKey } from '../lib/hubCopy'

/**
 * A body with its @mentions rendered as chips.
 *
 * Segments, never innerHTML. Nothing in this app sets dangerouslySetInnerHTML
 * and a thread carrying ~900 people's words is not the place to start — the
 * token is the only markup the body is allowed to contain, and it is turned
 * into an element here rather than into a string of HTML anywhere.
 *
 * A mention of the reader is marked, because "somebody said my name" is the
 * one thing worth finding at a glance in a thread of thirty.
 */
/**
 * 183. A reply's words, in the reader's language when One made a rendering of
 * them: the teal line above, and Show original swaps in the author's own words
 * — the same pair the post above it carries. Its own component because each
 * press is its own state.
 */
function ReplyBody({ c, me }: { c: WikiComment; me: string | null }) {
  const orig = useOriginal(c.translationStatus === 'machine', () => loadCommentOriginal(c.commentId))
  const text = orig.showingOriginal && orig.original ? orig.original.body : c.body ?? ''
  return (
    <>
      <TranslatedLine state={orig} />
      <Body text={text} mentions={c.mentions} me={me} />
    </>
  )
}

function Body({ text, mentions, me }: { text: string; mentions: MentionMap; me: string | null }) {
  return (
    <p className="thread__body">
      {splitMentions(text, mentions).map((s, i) =>
        s.kind === 'text' ? (
          <span key={i}>{s.text}</span>
        ) : (
          <span
            key={i}
            className={`mention${s.personId === me ? ' mention--me' : ''}`}
            // The name, never the id. Nobody is ever shown a uuid.
            title={s.name}
          >
            @{s.name}
          </span>
        ),
      )}
    </p>
  )
}

/**
 * A reply thread. FED-4's comments, rendered — the same component and the same
 * RPCs on both surfaces, because David chose one mechanism (8 Sep) and 022's
 * test says the question is whether the ACTS differ.
 *
 * ── IT MOVED OUT OF components/wiki/ ON 12 Sep, AND THAT IS THE POINT ───────
 *
 * It sat under wiki/ from the day it was written because Wiki One was the only
 * caller. The feed is now the second one, and a shared component living inside
 * one of its two callers' folders is the `content_creator` mistake in file
 * form: it reads as "the wiki's thread", which is exactly the belief that would
 * lead somebody to build a separate one for posts.
 *
 * ── ONE MECHANISM, TWO SURFACES ─────────────────────────────────────────────
 *
 * You REPLY to a question and you COMMENT on a post. Same act, same table, same
 * policies — different word, because on the feed "Reply" suggests you are
 * answering someone rather than adding to the conversation.
 *
 * ── WHY THIS TYPE GREW A BOOLEAN, WHICH THE OLD HEADER FORBADE ──────────────
 *
 * It said: "Anything that differs between the surfaces beyond these three
 * strings is a fork, and a fork here is two moderation surfaces for one kind of
 * row." That warning is still right and `allowOfficial` does not break it,
 * because it does not fork the ACT — every surface still writes the same row
 * through the same RPCs, and nothing about who may moderate, retract or hide
 * changes.
 *
 * What it forks is an OFFER, and the offer was wrong on one surface. "Mark
 * official" is Wiki One's curation: B18 settled that a curator vouching for an
 * answer is what makes it official, and `is_official` on a wiki reply means
 * "this is the current guidance, rely on it". A feed post is a DATED UTTERANCE
 * (040's distinction) — there is no curator of posts, nothing on the feed reads
 * an official badge as guidance, and yet the button appeared on every feed
 * comment for the four people holding `wiki.curate`, because the permission is
 * tenant-wide and the component did not know which surface it was on.
 *
 * David, 12 Sep: "remove mark official from feed replies."
 *
 * PRESENTATION, NOT A BOUNDARY. `mark_comment_official` still requires
 * `wiki.curate` and still accepts a feed comment id; a curator with a REST
 * client can still mark one. Nothing here may be relied on as a rule — if
 * official on a post reply is ever to be actually impossible, that is a check
 * in the function and a migration, not a prop.
 */
export type ThreadSurface = {
  /**
   * Which surface this is, and so which of lib/hubCopy.ts's THREAD sentences
   * the component renders (SHL-25). The words below are the ENGLISH column,
   * kept on the object so existing callers and assertions read the same; the
   * component itself renders THREAD[lang].surfaces[key], because Spanish
   * cannot build "This {noun} was retracted" from a noun (gender).
   */
  key: ThreadSurfaceKey
  /** The button that submits. 'Reply' / 'Comment'. */
  submit: string
  /** The empty state. */
  empty: string
  /** The textarea placeholder and its screen-reader label. */
  placeholder: string
  /** The word for one of these, lower case, for counts and expanders. */
  noun: string
  /** Whether a curator is offered "Mark official" here. See the header. */
  allowOfficial: boolean
  /**
   * Whether replies carry the ▲ vote (12 Oct, David's phone screenshot). On
   * Wiki One the vote ranks the answers under a question and earns its place;
   * on a post's comments it was a "▲ 0" beside the reactions on every row, a
   * second instrument for one job. Presentation only — `comments_for` still
   * orders by votes, and votes already cast on post comments stay.
   */
  allowVote: boolean
}

export const WIKI_SURFACE: ThreadSurface = {
  key: 'wiki',
  submit: THREAD.en.surfaces.wiki.submit,
  empty: THREAD.en.surfaces.wiki.empty,
  placeholder: THREAD.en.surfaces.wiki.placeholder,
  noun: 'reply',
  allowOfficial: true,
  allowVote: true,
}

export const POST_LABELS: ThreadSurface = {
  key: 'post',
  submit: THREAD.en.surfaces.post.submit,
  empty: THREAD.en.surfaces.post.empty,
  placeholder: THREAD.en.surfaces.post.placeholder,
  noun: 'comment',
  allowOfficial: false,
  allowVote: false,
}

/** Kept as the old name so existing imports still read. */
export type ThreadLabels = ThreadSurface

/**
 * How many nested replies are shown before the rest are folded away.
 *
 * David asked for "maybe 2-3". Three, because two plus an expander reads as a
 * truncation of almost nothing, and because the commonest real shape is a
 * question, an answer and a correction — which fits.
 */
export const NESTED_SHOWN = 3

/**
 * How many of a sub-thread to show, and how many are left.
 *
 * Exported and pure because it is the only arithmetic in this file that can be
 * wrong while still compiling and still rendering something plausible — the
 * same reason shapeFeed lives in lib/feed.ts rather than inside Home. The
 * off-by-one that matters is the one at exactly NESTED_SHOWN + 1: fold a single
 * reply away behind a control that says "Show 1 more reply" and you have spent
 * a row of chrome to hide a row of content.
 *
 * `hidden` is 0 (never negative) when everything fits, and the caller renders
 * no control at all in that case.
 */
export function foldReplies<T>(kids: T[], open: boolean): { shown: T[]; hidden: number } {
  const hidden = Math.max(0, kids.length - NESTED_SHOWN)
  return { shown: open || hidden === 0 ? kids : kids.slice(0, NESTED_SHOWN), hidden }
}

/**
 * @see ThreadSurface for why the surfaces differ and the behaviour does not.
 *
 * ── TWO LEVELS, AND THE LIMIT IS THE DATABASE'S ─────────────────────────────
 *
 * app.guard_comment_depth() refuses a third level. This component simply does
 * not offer a Reply control on a reply, which is courtesy — if it ever did, the
 * trigger would refuse the write rather than the tree growing quietly.
 *
 * ── THE NESTED REPLIES ARE FOLDED, AND THE FOLD SAYS SO (12 Sep) ────────────
 *
 * Every nested reply used to render, so one busy sub-thread pushed every other
 * top-level comment off the screen and a feed card became a wall. Three show;
 * the rest sit behind a control that states the number, because "Show more" on
 * a thread of thirty is a different offer from the same words on a thread of
 * four, and the reader deserves to know which they are looking at BEFORE they
 * decide to scroll.
 *
 * The fold is per parent and open state is held in a Set. It resets on
 * `contentId` change along with everything else.
 *
 * ── THE COMPOSE BAR IS AT THE BOTTOM NOW ────────────────────────────────────
 *
 * It was at the top, above the list, which put the invitation to write in front
 * of the thing there was to read. David asked for it below the replies; that is
 * also where every reader's eye already is when they finish the last one.
 *
 * ── THE LIST RE-READS AFTER EVERY ACTION ────────────────────────────────────
 *
 * Not an optimistic splice. Every one of these actions is an UPDATE under RLS,
 * and 028c is the worked example of a zero-row UPDATE returning success — a
 * locally edited list would show a change the database never made. Votes and
 * reactions are the deliberate exceptions: both RPCs return the authoritative
 * counts, so those numbers are updated in place without a refetch.
 *
 * ── A RETRACTED REPLY KEEPS ITS PLACE ───────────────────────────────────────
 *
 * comments_for() returns the row with a null body. The row stays so a thread
 * does not silently reshape around a gap, and so the reply that answered it
 * still makes sense. That went out and people saw it; the soft delete is the
 * record (020d).
 */
export function Thread({
  contentId,
  me,
  labels = WIKI_SURFACE,
  autoFocus = false,
  reviewing = false,
}: {
  contentId: string
  me: Me
  labels?: ThreadSurface
  /**
   * Put the caret in the compose box on mount. Set when the reader opened the
   * thread by pressing the comment bar on the post — they said what they came
   * to do, and landing them at the top of a list to scroll back down is the
   * kind of thing that makes a hub feel like paperwork.
   */
  autoFocus?: boolean
  /**
   * 127 (FED-25). Moderator mode is armed AND the reader holds
   * content.moderate. Shows what the content check said under every reply —
   * its verdict and reasoning (guidelines section 9) — and the three ways to
   * settle a flag or a check's take-down. The caller decides; this is gated on
   * the SWITCH, never on the permission (CLAUDE.md, "Curator mode means off").
   */
  reviewing?: boolean
}) {
  const lang = useHubLang()
  const t = THREAD[lang]
  const cw = CHECK_NOTE[lang]
  const words = t.surfaces[labels.key]
  const [comments, setComments] = useState<WikiComment[] | null>(null)
  // Drafts, not strings: the box shows "@Jamie Sharp" and carries the ids
  // beside it, and toStorage swaps them for tokens on the way out.
  const [draft, setDraft] = useState<MentionDraft>(EMPTY_DRAFT)
  const [replyTo, setReplyTo] = useState<string | null>(null)
  const [replyDraft, setReplyDraft] = useState<MentionDraft>(EMPTY_DRAFT)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [unfolded, setUnfolded] = useState<Set<string>>(() => new Set())

  const canModerate = can(me, 'content.moderate')
  const canCurate = can(me, 'wiki.curate') && labels.allowOfficial

  /* 127. The check's notes (moderator mode) and, for the reader's OWN replies
     the check took down, why (guideline 8). Both are side reads: a thread
     never waits on them, and a failure leaves them out. */
  const [notes, setNotes] = useState<Map<string, Note> | null>(null)
  const [whyDown, setWhyDown] = useState<Map<string, string | null>>(() => new Map())

  async function refresh() {
    try {
      setComments(await loadComments(contentId))
      setError(null)
    } catch (e) {
      setError(messageOf(e))
    }
    if (reviewing) {
      loadCheckNotes([contentId]).then((n) => setNotes(replyNotes(n))).catch(() => setNotes(null))
    }
  }

  useEffect(() => {
    let live = true
    if (!reviewing) {
      setNotes(null)
      return
    }
    loadCheckNotes([contentId])
      .then((n) => live && setNotes(replyNotes(n)))
      .catch(() => live && setNotes(null))
    return () => {
      live = false
    }
  }, [contentId, reviewing, comments?.length])

  const myDownIds = (comments ?? [])
    .filter((c) => c.isRetracted && c.authorPersonId === me.personId)
    .map((c) => c.commentId)
    .join(',')
  useEffect(() => {
    let live = true
    if (!myDownIds) return
    loadRetractionReasons(myDownIds.split(','))
      .then((m) => live && setWhyDown(m))
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [myDownIds])

  useEffect(() => {
    let live = true
    loadComments(contentId)
      .then((c) => live && setComments(c))
      .catch((e: Error) => live && setError(e.message))
    /* 183, 185. The backstop: a reply is translated by the check right after
       it is written, and a two-minute sweep catches the rest — so this
       usually finds nothing to do (one read, no model call). If it stored
       something, the thread re-reads. A reply posted here is NOT sent from
       this component: the check does it, and the onChecked re-read shows it. */
    void requestCommentTranslation(contentId).then((n) => {
      if (live && n > 0) void loadComments(contentId).then((c) => live && setComments(c)).catch(() => undefined)
    })
    return () => {
      live = false
    }
    // The focus moved INTO MentionBox with 059's compose control. It used to be
    // taken here, after the list resolved, so the caret did not land in a box
    // floating under nothing — MentionBox takes it on mount instead, and the
    // form renders below a list that is still "Loading…" for a moment rather
    // than absent, so there is context either way.
  }, [contentId])

  async function act(fn: () => Promise<unknown>) {
    setBusy(true)
    setError(null)
    try {
      await fn()
      await refresh()
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  if (comments === null) return <p className="empty">{words.loading}</p>

  const top = comments.filter((c) => c.parentCommentId === null)
  const repliesTo = (id: string) => comments.filter((c) => c.parentCommentId === id)

  const render = (c: WikiComment, isReply: boolean) => (
    <li className={`thread__item${isReply ? ' thread__item--reply' : ''}`} key={c.commentId}>
      <div className="thread__row">
        <div className="thread__main">
          <p className="thread__meta">
            <span className="thread__author">{c.authorName ?? t.someone}</span>
            {c.isOfficial && <span className="pill pill--star">{t.official}</span>}
            {c.isStale && <span className="pill pill--stale">{t.stale}</span>}
            {c.editedAt && <span>{t.edited}</span>}

            {/* OFFICIAL AND CHECKED ARE DIFFERENT THINGS, and only one of them
                belongs next to the other.

                `is_official` is a curator saying "this is right, rely on it".
                `moderation_status` is what the safety CHECK concluded — and
                since 127 (FED-25) a reply is graded just after it goes out,
                so "not checked" is a reply the check has not reached yet, one
                on a private support thread (never graded), or one it could not
                grade.

                Showing "not checked" beside "★ Official" would tell the reader
                a named human's endorsement is somehow provisional, which is
                backwards: a human reading it is STRONGER than a machine reading
                it, and B18 settled exactly that for Wiki One answers. So the
                unchecked badge is suppressed once a curator has vouched.

                A FLAG is not suppressed. Official-and-flagged is a real
                disagreement between a person and the check, and hiding it would
                be hiding the one state worth a second look. */}
            {c.moderationStatus === 'failed_open' && !c.isOfficial && !c.isRetracted && (
              <span className="pill pill--draft">{t.notChecked}</span>
            )}
            {c.moderationStatus === 'flagged' && !c.isRetracted && (
              <span className="pill pill--draft">{t.flagged}</span>
            )}
          </p>
          {c.isRetracted ? (
            <p className="thread__gone">{words.retracted}</p>
          ) : c.isHidden ? (
            <p className="thread__gone">
              {t.hiddenByModerator}{canModerate && <> {c.body}</>}
            </p>
          ) : (
            <ReplyBody c={c} me={me.personId} />
          )}

          {/* 127, guideline 8. The author of a reply the CHECK took down is
              told why, in the reply's own language (the model writes the
              reason in it). A person's retraction has no verdict and shows
              nothing here. */}
          {c.isRetracted && whyDown.has(c.commentId) && (
            <p className="thread__checked">
              {cw.yourReplyTakenDown} {whyDown.get(c.commentId) ?? cw.noReasonGiven}
            </p>
          )}

          {/* 127. Moderator mode: what the check said about this reply. A
              reply with no verdict yet reads "No check on record" only once
              the notes have loaded — undefined while they load. */}
          {reviewing && notes && (
            <CheckNote
              note={notes.get(c.commentId) ?? null}
              isReply
              onResolved={refresh}
            />
          )}

          {/* 060. Inert chips, same as FeedPost: a tag that looks clickable and
              is not is worse than one that does not, and browse-by-tag is
              FED-18. They render below the words because the words are the
              point — on a post the tags sit under the body for the same
              reason. */}
          {c.tags.length > 0 && !c.isRetracted && (
            <ul className="posttags posttags--reply">
              {c.tags.map((t) => (
                <li key={t.slug} className="tagchip">{`#${t.label}`}</li>
              ))}
            </ul>
          )}

          <div className="thread__actions">
            {/* The vote moved OUT of the left gutter and into this row on
                12 Sep. It was a bordered tile in a 2.4rem column reserved on
                every reply, which made a ranking control the widest thing in a
                row whose subject is what somebody wrote. No vote on a retracted
                reply: there is nothing left to endorse and the count would
                outlive the words. */}
            {labels.allowVote && !c.isRetracted && (
              <VoteButton
                size="sm"
                votes={c.votes}
                iVoted={c.iVoted}
                target={{ commentId: c.commentId }}
                onChange={(votes, iVoted) =>
                  setComments((cur) =>
                    (cur ?? []).map((x) =>
                      x.commentId === c.commentId ? { ...x, votes, iVoted } : x,
                    ),
                  )
                }
              />
            )}

            {/* 058. Same component and same four kinds as the post above, at
                thread size. A vote and a reaction are different instruments —
                the vote ranks this thread and a reaction says how it landed —
                which is why both are here and why comments_for's ORDER BY does
                not read the reactions. */}
            {!c.isRetracted && (
              <ReactionBar
                key={c.commentId}
                compact
                target={{ commentId: c.commentId }}
                counts={c.reactions}
                mine={c.myReaction}
              />
            )}

            {!isReply && !c.isRetracted && (
              <button
                type="button"
                className="thread__act"
                onClick={() => setReplyTo(replyTo === c.commentId ? null : c.commentId)}
              >
                {t.reply}
              </button>
            )}
            {/* THE OTHER ACTS LIVE BEHIND ⋯ (12 Oct, David's phone screenshot:
                Hide and Retract were full-size bordered buttons that wrapped
                every reply onto a second row). Reply stays out because it is
                what readers do; these are what an author or a moderator does
                now and then. Mark official is Wiki One only — see the header;
                the permission still permits it on a post, this is the offer,
                not the rule. Retraction is permanent — the guard refuses
                un-retracting — so it asks first; hiding is the reversible
                instrument and does not. 030's asymmetry, one level down. */}
            {!c.isRetracted && (
              <ReplyMenu
                label={t.moreActions}
                disabled={busy}
                items={[
                  ...(canCurate
                    ? [{ key: 'official', label: c.isOfficial ? t.unmarkOfficial : t.markOfficial,
                         run: () => void act(() => markCommentOfficial(c.commentId, !c.isOfficial)) }]
                    : []),
                  ...(canModerate
                    ? [{ key: 'hide', label: c.isHidden ? t.unhide : t.hide,
                         run: () => void act(() => moderateComment(c.commentId, c.isHidden ? 'unhide' : 'hide')) }]
                    : []),
                  ...(c.authorPersonId === me.personId || canModerate
                    ? [{ key: 'retract', label: t.retract,
                         run: () => {
                           if (window.confirm(words.confirmRetract)) void act(() => retractComment(c.commentId))
                         } }]
                    : []),
                ]}
              />
            )}
          </div>

          {replyTo === c.commentId && (
            <form
              className="thread__form"
              onSubmit={(e) => {
                e.preventDefault()
                if (!replyDraft.text.trim()) return
                void act(async () => {
                  await addComment(contentId, toStorage(replyDraft), c.commentId, () => void refresh())
                  setReplyDraft(EMPTY_DRAFT)
                  setReplyTo(null)
                  // Open the fold so the thing just written is visible. Posting
                  // into a collapsed group and being shown nothing reads as the
                  // reply having failed.
                  setUnfolded((cur) => new Set(cur).add(c.commentId))
                })
              }}
            >
              {/* Both boxes are MentionBox, deliberately. One compose control
                  with one @ implementation — 046's rule, and the reason the
                  invite form's two paths became two buttons rather than two
                  inputs. A reply box without mentions while the box eight
                  pixels above it has them is the kind of difference nobody can
                  explain to a crew. */}
              <MentionBox
                value={replyDraft}
                onChange={setReplyDraft}
                placeholder={t.replyPlaceholder}
                ariaLabel={t.reply}
              />
              <button className="btn btn--primary btn--sm" disabled={busy || !replyDraft.text.trim()}>
                {t.postReply}
              </button>
            </form>
          )}
        </div>
      </div>

      {!isReply && renderNested(c.commentId)}
    </li>
  )

  /**
   * The nested replies under one top-level comment, folded past NESTED_SHOWN.
   *
   * The expander states the NUMBER rather than saying "Show more", because the
   * reader is deciding whether to commit to scrolling and four hidden replies
   * and forty are different decisions. Collapsing again is offered too: a fold
   * that only opens is a fold you regret pressing.
   */
  function renderNested(parentId: string) {
    const kids = repliesTo(parentId)
    if (kids.length === 0) return null

    const open = unfolded.has(parentId)
    const { shown, hidden } = foldReplies(kids, open)

    return (
      <>
        <ul className="thread thread--nested">{shown.map((r) => render(r, true))}</ul>

        {hidden > 0 && (
          <button
            type="button"
            className="thread__more"
            aria-expanded={open}
            onClick={() =>
              setUnfolded((cur) => {
                const next = new Set(cur)
                if (next.has(parentId)) next.delete(parentId)
                else next.add(parentId)
                return next
              })
            }
          >
            <span className="thread__more-rule" aria-hidden="true" />
            {/* "Replies" on both surfaces, deliberately, even where the
                top level is called a comment: these are replies TO a comment,
                and calling them comments would suggest the fold hides more of
                the outer list. */}
            {open ? t.showFewer : t.showMore(hidden)}
          </button>
        )}
      </>
    )
  }

  return (
    <div className="thread-wrap">
      {error && (
        <p className="roles__error" role="alert">
          {error}
        </p>
      )}

      {top.length === 0 ? (
        <p className="empty">{words.empty}</p>
      ) : (
        <ul className="thread">{top.map((c) => render(c, false))}</ul>
      )}

      {/* BELOW the list (12 Sep). It was above it, which put the invitation to
          write in front of the thing there was to read. */}
      <form
        className="thread__form thread__form--foot"
        onSubmit={(e) => {
          e.preventDefault()
          if (!draft.text.trim()) return
          void act(async () => {
            await addComment(contentId, toStorage(draft), undefined, () => void refresh())
            setDraft(EMPTY_DRAFT)
          })
        }}
      >
        <label className="sr-only" htmlFor={`reply-${contentId}`}>
          {words.placeholder.replace('…', '')}
        </label>
        <MentionBox
          id={`reply-${contentId}`}
          value={draft}
          onChange={setDraft}
          placeholder={words.placeholder}
          ariaLabel={words.placeholder.replace('…', '')}
          autoFocus={autoFocus}
        />
        <button className="btn btn--primary btn--sm" disabled={busy || !draft.text.trim()}>
          {words.submit}
        </button>
      </form>
    </div>
  )
}

/**
 * The ⋯ beside Reply (12 Oct). A menu, not a dialog: useDismiss closes it on
 * Escape or an outside press, and it renders nothing at all when the reader
 * may do none of the acts — an empty menu is a control that lies.
 */
function ReplyMenu({
  label,
  items,
  disabled,
}: {
  label: string
  items: { key: string; label: string; run: () => void }[]
  disabled: boolean
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useDismiss(ref, open, () => setOpen(false))
  if (items.length === 0) return null
  return (
    <div ref={ref} className="replymenu">
      <button
        type="button"
        className="thread__act replymenu__btn"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        <span aria-hidden="true">⋯</span>
      </button>
      {open && (
        <ul className="replymenu__list" role="menu" aria-label={label}>
          {items.map((it) => (
            <li key={it.key} role="none">
              <button
                type="button"
                role="menuitem"
                className="replymenu__item"
                onClick={() => {
                  setOpen(false)
                  it.run()
                }}
              >
                {it.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
